import type { FeatureFlagData } from '@sb/feathers-types/featureFlags';
import { FeatureFlagDataZod } from '@sb/feathers-types/featureFlags';
import * as log from '@sb/log';

import { readJSONFileWithFallback } from './util';

const ns = log.namespace('featureFlags');

const path = '/etc/standardbots/configuration/feature_flags.json';
let cache: FeatureFlagData | undefined;

export const updateFeatureFlagsCache = async (): Promise<void> => {
  const rawData = await readJSONFileWithFallback<Partial<FeatureFlagData>>(
    path,
    {},
  );

  // Parse through Zod schema to apply all schema defaults for missing keys.
  // This ensures that flags with default-true values (like useTeachPendant)
  // are properly set in the cache even if they're not in the JSON file.
  cache = FeatureFlagDataZod.parse(rawData);
};

export const loadFeatureFlags = async (
  refreshCache = false,
): Promise<FeatureFlagData> => {
  if (!cache || refreshCache) {
    await updateFeatureFlagsCache();
  }

  // Since updateFeatureFlagsCache always sets cache, it's safe to assert it's non-undefined
  return cache as FeatureFlagData;
};

export const getFeatureFlag = async <T extends keyof FeatureFlagData>(
  flag: T,
  refreshCache = false,
): Promise<FeatureFlagData[T]> => {
  const flags = await loadFeatureFlags(refreshCache);

  return flags[flag];
};

/**
 * Synchronous version of getFeatureFlag.  Feature flags must be loaded before
 * @param flag
 * @returns
 */
export const getFeatureFlagSync = <T extends keyof FeatureFlagData>(
  flag: T,
  defaultValue: FeatureFlagData[T],
): FeatureFlagData[T] => {
  if (!cache) {
    log.warn(ns`getFeatureFlagSync`, 'Feature flags not loaded yet', {
      flag,
      defaultValue,
    });

    // this default value is only relevant if there is no cache, otherwise
    // the cache itself knows the true default value
    return defaultValue;
  }

  // if flag is not in cache, return defaultValue
  if (cache[flag] === undefined) {
    log.warn(ns`getFeatureFlagSync`, 'Flag not found in cache (unexpected)', {
      flag,
      defaultValue,
    });

    return defaultValue;
  }

  return cache[flag];
};
